import React, { useState } from 'react';
import {
    Card,
    Input,
    Select,
    Space,
    Tag,
    Typography,
    Row,
    Col,
    DatePicker,
    Button,
    Tooltip,
    Alert,
} from 'antd';
import {
    HistoryOutlined,
    SearchOutlined,
    ReloadOutlined,
    ExportOutlined,
    UserOutlined,
    AppstoreOutlined,
} from '@ant-design/icons';
import { useGetAdminEventsQuery } from '../../services/nestjs-base-api';
import { useAuth } from '../../hooks/useAuth';
import { ResponsiveTable } from '../components/ResponsiveTable';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { EVENT_TYPE_COLORS, formatEventType } from '../utils/eventTypeUtils';

dayjs.extend(relativeTime);

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

interface IEventLog {
    id: number;
    eventType: string;
    userId?: number;
    organisationId?: number;
    entityType?: string;
    entityId?: number;
    metadata?: any;
    ipAddress?: string;
    userAgent?: string;
    createdAt: string;
    user?: {
        id: number;
        email: string;
        firstName?: string;
        lastName?: string;
    };
    organisation?: {
        id: number;
        name: string;
    };
}

// IMPORTANT: When adding new event types to the backend (src/events/enums/event-type.enum.ts),
// you MUST also add them to this list to make them available in the event log filters!
// Also update the list in:
// - src/components/Profile/Notifications.tsx (ALL_EVENT_TYPES)
// - src/admin/utils/eventTypeUtils.ts (EVENT_TYPE_COLORS)
const EVENT_TYPE_OPTIONS = [
    'USER_REGISTERED',
    'USER_LOGIN',
    'USER_LOGOUT',
    'USER_PROFILE_UPDATED',
    'USER_PASSWORD_CHANGED',
    'USER_PASSWORD_SET',
    'USER_IMPERSONATED',
    'USER_IMPERSONATION_STARTED',
    'USER_IMPERSONATION_ENDED',
    'USER_LOGIN_FAILED',
    'USER_ACCOUNT_LOCKED',
    'USER_ACCOUNT_UNLOCKED',
    'ORGANIZATION_CREATED',
    'ORGANIZATION_UPDATED',
    'ORGANIZATION_STATUS_UPDATED',
    'ORGANIZATION_SETTINGS_CHANGED',
    'ORGANIZATION_FEATURES_UPDATED',
    'ORGANIZATION_DELETED',
    'BRAND_CREATED',
    'BRAND_UPDATED',
    'BRAND_NAME_UPDATED',
    'BRAND_DELETED',
    // Cohort events
    'COHORT_CREATED',
    'COHORT_UPDATED',
    'COHORT_DEACTIVATED',
    'COHORT_DELETED',
    'SURVEY_CREATED',
    'SURVEY_UPDATED',
    'SURVEY_PUBLISHED',
    'SURVEY_PAUSED',
    'SURVEY_DELETED',
    'SURVEY_RESPONSE_RECEIVED',
    'SURVEY_RESPONSE_DELETED',
    'SURVEY_LINK_NOT_FOUND',
    'SURVEY_LINK_SALVAGED',
    'ADMIN_USER_CREATED',
    'ADMIN_USER_UPDATED',
    'ADMIN_USER_DELETED',
    'ADMIN_USER_PASSWORD_RESET',
    'ORGANIZATION_USER_CREATED',
    'API_KEY_CREATED',
    'API_KEY_REVOKED',
    'PUBLIC_API_PARTICIPANTS_FETCHED',
    'BILLING_EVENT',
    'DATA_EXPORT_REQUESTED',
    'SYSTEM_MAINTENANCE',
    'DATABASE_BACKUP_CREATED',
    'DATABASE_BACKUP_COMPLETED',
    'DATABASE_BACKUP_FAILED',
    'DATABASE_BACKUP_DELETED',
    'DATABASE_BACKUP_RETENTION_CLEANUP',
    'RATE_LIMIT_SETTINGS_UPDATED',
    'RATE_LIMIT_EXCEEDED',
    // Message events
    'MESSAGE_SENT_BY_ADMIN',
    'MESSAGE_RECEIVED',
    'MESSAGE_READ',
    'MESSAGE_MARKED_UNREAD',
    'MESSAGE_STARRED',
    'MESSAGE_UNSTARRED',
    'MESSAGE_ARCHIVED',
    'MESSAGE_UNARCHIVED',
    'MESSAGE_DELETED',
    'MESSAGE_PERMANENTLY_DELETED',
    'MESSAGES_BULK_UPDATED',
    'MESSAGES_ALL_MARKED_READ',
    'MESSAGES_TRASH_EMPTIED',
    'MESSAGE_PREFERENCES_UPDATED',
    'MESSAGE_UNSUBSCRIBED',
    'MESSAGE_TYPE_UNSUBSCRIBED',
    // Response Monitoring events
    'RESPONSE_MONITORING_CHECK_TRIGGERED',
    'RESPONSE_MONITORING_MESSAGE_GENERATED',
    'RESPONSE_MONITORING_MESSAGE_APPROVED',
    'RESPONSE_MONITORING_MESSAGE_REJECTED',
    'RESPONSE_MONITORING_MESSAGE_SENT',
    'RESPONSE_MONITORING_MESSAGE_REGENERATED',
    'RESPONSE_MONITORING_TEST_EMAIL_SENT',
    'RESPONSE_MONITORING_CONFIG_UPDATED',
    'RESPONSE_MONITORING_SCHEDULE_UPDATED',
    // AI Expert events
    'AI_EXPERT_CONVERSATION_CREATED',
    'AI_EXPERT_CONVERSATION_DELETED',
    'AI_EXPERT_MESSAGE_SENT',
    'AI_EXPERT_DOCUMENT_UPLOADED',
    'AI_EXPERT_DOCUMENT_DELETED',
    'AI_EXPERT_CONFIG_UPDATED',
    'AI_EXPERT_TOGGLED',
    'AI_EXPERT_CREATED',
    // Brand Knowledge Base events
    'BRAND_KNOWLEDGE_PROFILE_UPDATED',
    'BRAND_KNOWLEDGE_DOCUMENT_ADDED',
    'BRAND_KNOWLEDGE_DOCUMENT_UPDATED',
    'BRAND_KNOWLEDGE_DOCUMENT_DELETED',
    // Embedded CRM events
    'CRM_COMPANY_CREATED',
    'CRM_COMPANY_UPDATED',
    'CRM_COMPANY_LINKED',
    'CRM_COMPANY_UNLINKED',
    'CRM_STAGE_CHANGED',
    'CRM_NOTE_ADDED',
    'CRM_TASK_CREATED',
    'CRM_TASK_COMPLETED',
    'CRM_SHEET_SYNC_COMPLETED',
    'CRM_SHEET_SYNC_FAILED',
    'CRM_EMAIL_SENT',
    'PLATFORM_EMAIL_SENT',
    'CRM_EMAIL_RECEIVED',
    'CRM_INBOX_SYNCED',
    'CRM_REMINDER_SENT',
    // The Hypothesis events (SYR-90)
    'HYPOTHESIS_RESEARCH_STARTED',
    'HYPOTHESIS_RESEARCH_COMPLETED',
    'HYPOTHESIS_RESEARCH_INSUFFICIENT_DATA',
    'HYPOTHESIS_AWAITING_REVIEWS',
    'HYPOTHESIS_RESEARCH_FAILED',
    'HYPOTHESIS_FORMED',
    'HYPOTHESIS_SUBJECT_DELETED',
    'HYPOTHESIS_TEASE_SENT',
    'HYPOTHESIS_DISMISSED',
    'HYPOTHESIS_CONFIRMED',
    'HYPOTHESIS_OVERTURNED',
    'HYPOTHESIS_REVEAL_SENT',
    // Auto-Concierge (SYR-105)
    'CONCIERGE_PACK_QUEUED',
    'CONCIERGE_PACK_GENERATED',
    'CONCIERGE_PACK_GENERATION_FAILED',
    'CONCIERGE_PACK_EDITED',
    'CONCIERGE_PACK_SENT',
    'CONCIERGE_PACK_KILLED',
    'CONCIERGE_IDENTITY_VERIFICATION_SET',
    'CONCIERGE_OFFER_SET',
    // Charter Cohort (SYR-106)
    'CHARTER_APPLICATION_SUBMITTED',
    'CHARTER_APPLICATION_SPAM_REJECTED',
    'CHARTER_APPLICATION_ACCEPTED',
    'CHARTER_APPLICATION_HELD',
    'CHARTER_APPLICATION_UNSUPPORTED',
    'CHARTER_APPLICATION_UNREVIEWED',
    'CHARTER_ORGANISATION_PROVISIONED',
    'CHARTER_TRIAL_START_BLOCKED',
    // Pricing strategy (SYR-109): a no-free/no-trial signup completed its
    // first checkout — the registration-to-revenue conversion signal.
    'PAID_SIGNUP_COMPLETED',
    // Public Hypothesis lead generator (SYR-107). QUEUED_FOR_REVIEW is the one
    // worth subscribing to: a real brand wanted this and did not clear the
    // gate, which is a conversation rather than a rejection.
    'PUBLIC_HYPOTHESIS_REQUESTED',
    'PUBLIC_HYPOTHESIS_PIN_VERIFIED',
    'PUBLIC_HYPOTHESIS_QUEUED_FOR_REVIEW',
    'PUBLIC_HYPOTHESIS_BLOCKED',
    'PUBLIC_HYPOTHESIS_GENERATED',
    'PUBLIC_HYPOTHESIS_SERVED_CACHED',
    'PUBLIC_HYPOTHESIS_RESULT_SENT',
    'PUBLIC_HYPOTHESIS_INSUFFICIENT_DATA',
    'PUBLIC_HYPOTHESIS_FAILED',
    'PUBLIC_HYPOTHESIS_SPAM_REJECTED',
    'PUBLIC_HYPOTHESIS_UNSUBSCRIBED',
    'PUBLIC_HYPOTHESIS_LEAD_TO_CRM',
    'PUBLIC_HYPOTHESIS_LEAD_DELETED',
    'PUBLIC_HYPOTHESIS_RESULT_LINK_COPIED',
    'HYPOTHESIS_PROVIDER_DEGRADED',
    'PUBLIC_HYPOTHESIS_VIDEO_PLAYED',
    // Email delivery lifecycle events (SES webhook)
    'EMAIL_DELIVERED',
    'EMAIL_OPENED',
    'EMAIL_CLICKED',
    'EMAIL_BOUNCED',
    'EMAIL_COMPLAINT_RECEIVED',
    // Dashboard AI events
    'DASHBOARD_AI_INSIGHT_GENERATED',
    'DASHBOARD_AI_CONFIG_UPDATED',
    'DASHBOARD_AI_CACHE_CLEARED',
    // Advice AI events
    'ADVICE_AI_INSIGHT_GENERATED',
    'ADVICE_AI_CONFIG_UPDATED',
    'ADVICE_AI_CACHE_CLEARED',
    // Orbital AI events
    'ORBITAL_AI_ITEMS_GENERATED',
    'ORBITAL_AI_CONFIG_UPDATED',
    'ORBITAL_AI_CACHE_CLEARED',
    // Building Blocks AI events
    'BUILDING_BLOCKS_AI_INSIGHTS_GENERATED',
    'BUILDING_BLOCKS_AI_CONFIG_UPDATED',
    'BUILDING_BLOCKS_AI_CACHE_CLEARED',
    // AI System Config events
    'AI_SYSTEM_CONFIG_UPDATED',
    'AI_PROVIDER_TEST_SUCCESS',
    'AI_PROVIDER_TEST_FAILED',
    'AI_PROVIDER_FALLBACK_USED',
    // AI Quota events
    'AI_QUOTA_ALERT_SENT',
    'AI_QUOTA_WARNING_SENT',
    'AI_QUOTA_EXCEEDED',
    'AI_QUOTA_UPDATED',
    'AI_QUOTA_RESET',
    // Brand Analytics events
    'BRAND_ANALYTICS_VIEWED',
    'BRAND_ANALYTICS_DRILLDOWN_ACCESSED',
    // Custom Brand Charts events
    'CUSTOM_CHART_CREATED',
    'CUSTOM_CHART_UPDATED',
    'CUSTOM_CHART_DELETED',
    'CUSTOM_CHARTS_REORDERED',
    // Scheduled Job Lock events
    'SCHEDULED_JOB_STARTED',
    'SCHEDULED_JOB_COMPLETED',
    'SCHEDULED_JOB_FAILED',
    'SCHEDULED_JOB_LOCK_ACQUIRED',
    'SCHEDULED_JOB_LOCK_RELEASED',
    'SCHEDULED_JOB_LOCK_FAILED',
    'SCHEDULED_JOB_LOCK_EXPIRED',
    'SCHEDULED_JOB_FORCE_UNLOCKED',
    // CloudWatch Logs events
    'CLOUDWATCH_LOGS_ACCESSED',
    'CLOUDWATCH_LOGS_TAILED',
    'CLOUDWATCH_LOGS_TAIL_ENDED',
    'CLOUDWATCH_LOGS_SEARCHED',
    // Support Ticket events
    'TICKET_CREATED',
    'TICKET_UPDATED',
    'TICKET_COMMENT_ADDED',
    'TICKET_ATTACHMENT_UPLOADED',
    'TICKET_ATTACHMENT_DELETED',
    // Free Trial events
    'FREE_TRIAL_STARTED',
    'FREE_TRIAL_RESPONSE_REQUIREMENT_MET',
    'FREE_TRIAL_RESPONSE_REQUIREMENT_FAILED',
    'FREE_TRIAL_DOWNGRADED',
    'FREE_TRIAL_CONVERTED',
    'FREE_TRIAL_EXPIRED',
    'FREE_TRIAL_NOTIFICATION_SENT',
    'FREE_TRIAL_RESET',
    'FREE_TRIAL_NOTIFICATION_SUPPRESSED',
    'WAITLIST_ENTRY_CREATED',
    'WAITLIST_ENTRY_UPDATED',
    'WAITLIST_STATUS_CHANGED',
    'CORPORATE_SITE_PUBLISHED',
    'CORPORATE_SITE_REVERTED',
    // Response Integrity events (SYR-48)
    'RESPONSE_INTEGRITY_SCORED',
    'RESPONSE_INTEGRITY_FLAGGED',
    'RESPONSE_INTEGRITY_REJECTED_MANUALLY',
    'RESPONSE_INTEGRITY_CONFIRMED_CLEAN',
    'RESPONSE_INTEGRITY_RESCORED',
    'RESPONSE_INTEGRITY_BULK_REJECTED',
    'RESPONSE_INTEGRITY_BULK_CONFIRMED_CLEAN',
    'RESPONSE_INTEGRITY_BULK_RESCORED',
    'RESPONSE_INTEGRITY_DETECTOR_CONFIG_UPDATED',
    'RESPONSE_INTEGRITY_PROFILE_UPDATED',
    'RESPONSE_INTEGRITY_LLM_BUDGET_ALERT',
    'RESPONSE_INTEGRITY_LLM_BUDGET_EXHAUSTED',
    'RESPONSE_INTEGRITY_EVIDENCE_ARCHIVED',
    // Panel Chargeback events (SYR-48)
    'PANEL_CHARGEBACK_PREPARED',
    'PANEL_CHARGEBACK_SUBMITTED',
    'PANEL_CHARGEBACK_RESOLVED',
];

export const AdminEventsPage: React.FC = () => {
    const { user } = useAuth();
    const [searchText, setSearchText] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(100);
    const [eventTypeFilter, setEventTypeFilter] = useState<string>('');
    const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

    // Build query parameters
    const queryParams = {
        jwtToken: user?.jwtToken || '',
        page: currentPage,
        limit: pageSize,
        ...(eventTypeFilter && { eventType: eventTypeFilter }),
        ...(searchText && { search: searchText }),
        ...(dateRange && {
            fromDate: dateRange[0].startOf('day').toISOString(),
            toDate: dateRange[1].endOf('day').toISOString(),
        }),
    };

    const { data: eventsData, isLoading, error, refetch } = useGetAdminEventsQuery(queryParams);

    const handleSearch = (value: string) => {
        setSearchText(value);
        setCurrentPage(1); // Reset to first page when searching
    };

    const handleEventTypeChange = (value: string) => {
        setEventTypeFilter(value);
        setCurrentPage(1);
    };

    const handleDateRangeChange = (dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null) => {
        if (dates && dates[0] && dates[1]) {
            setDateRange([dates[0], dates[1]]);
        } else {
            setDateRange(null);
        }
        setCurrentPage(1);
    };

    const handleTableChange = (page: number, size: number) => {
        setCurrentPage(page);
        setPageSize(size);
    };

    const handleRefresh = () => {
        refetch();
    };


    const columns: ColumnsType<IEventLog> = [
        {
            title: 'Time',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 150,
            sorter: true,
            render: (createdAt: string) => (
                <div>
                    <div className="text-sm font-medium">
                        {dayjs(createdAt).format('MMM DD, YYYY')}
                    </div>
                    <div className="text-xs text-gray-500">
                        {dayjs(createdAt).format('HH:mm:ss')}
                    </div>
                    <div className="text-xs text-gray-400">
                        {dayjs(createdAt).fromNow()}
                    </div>
                </div>
            ),
        },
        {
            title: 'Event Type',
            dataIndex: 'eventType',
            key: 'eventType',
            width: 200,
            render: (eventType: string) => (
                <Tag color={EVENT_TYPE_COLORS[eventType] || 'default'}>
                    {formatEventType(eventType)}
                </Tag>
            ),
        },
        {
            title: 'User',
            key: 'user',
            width: 200,
            render: (_, record: IEventLog) => (
                <div>
                    <div className="flex items-center space-x-2">
                        <UserOutlined className="text-gray-400" />
                        <span className="text-sm font-medium">
                            {record.user?.email || 'System'}
                        </span>
                    </div>
                    {record.user?.firstName && record.user?.lastName && (
                        <div className="text-xs text-gray-500">
                            {record.user.firstName} {record.user.lastName}
                        </div>
                    )}
                </div>
            ),
        },
        {
            title: 'Organization',
            key: 'organisation',
            width: 200,
            render: (_, record: IEventLog) => (
                <div>
                    {record.organisation ? (
                        <div className="flex items-center space-x-2">
                            <AppstoreOutlined className="text-gray-400" />
                            <span className="text-sm">
                                {record.organisation.name}
                            </span>
                        </div>
                    ) : (
                        <span className="text-gray-400 text-sm">—</span>
                    )}
                </div>
            ),
        },
        {
            title: 'Entity',
            key: 'entity',
            width: 150,
            render: (_, record: IEventLog) => (
                <div>
                    {record.entityType && record.entityId ? (
                        <div className="text-sm">
                            <div className="font-medium">{record.entityType}</div>
                            <div className="text-gray-500">ID: {record.entityId}</div>
                        </div>
                    ) : (
                        <span className="text-gray-400 text-sm">—</span>
                    )}
                </div>
            ),
        },
        {
            title: 'IP Address',
            dataIndex: 'ipAddress',
            key: 'ipAddress',
            width: 120,
            render: (ipAddress: string) => (
                <span className="text-xs font-mono text-gray-600">
                    {ipAddress || '—'}
                </span>
            ),
        },
        {
            title: 'Metadata',
            dataIndex: 'metadata',
            key: 'metadata',
            width: 200,
            render: (metadata: any) => (
                <div>
                    {metadata && Object.keys(metadata).length > 0 ? (
                        <Tooltip 
                            title={
                                <pre style={{ 
                                    margin: 0, 
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    maxHeight: '400px',
                                    overflow: 'auto'
                                }}>
                                    {JSON.stringify(metadata, null, 2)}
                                </pre>
                            }
                            overlayStyle={{
                                maxWidth: '600px',
                                width: 'max-content'
                            }}
                            placement="topLeft"
                        >
                            <Tag color="blue" className="cursor-pointer">
                                View Details
                            </Tag>
                        </Tooltip>
                    ) : (
                        <span className="text-gray-400 text-sm">—</span>
                    )}
                </div>
            ),
        },
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between" style={{ marginBottom: '32px' }}>
                <div>
                    <Title level={2} className="!mb-0">
                        <HistoryOutlined className="mr-3" style={{ marginRight: '8px' }} />
                        Event Log
                    </Title>
                    <Text type="secondary">
                        Monitor all system events and user activities
                    </Text>
                </div>
                <Space>
                    <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
                        Refresh
                    </Button>
                    <Button icon={<ExportOutlined />} disabled>
                        Export
                    </Button>
                </Space>
            </div>

            {/* Filters */}
            <Card>
                <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} sm={12} md={8}>
                        <Search
                            placeholder="Search events, users, organizations..."
                            allowClear
                            enterButton={<SearchOutlined />}
                            onSearch={handleSearch}
                            onChange={(e) => !e.target.value && handleSearch('')}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Select
                            placeholder="Event Type"
                            allowClear
                            style={{ width: '100%' }}
                            value={eventTypeFilter || undefined}
                            onChange={handleEventTypeChange}
                        >
                            {EVENT_TYPE_OPTIONS.map(eventType => (
                                <Option key={eventType} value={eventType}>
                                    <Tag color={EVENT_TYPE_COLORS[eventType] || 'default'}>
                                        {formatEventType(eventType)}
                                    </Tag>
                                </Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <RangePicker
                            style={{ width: '100%' }}
                            value={dateRange}
                            onChange={handleDateRangeChange}
                            format="YYYY-MM-DD"
                            placeholder={['Start Date', 'End Date']}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={4}>
                        <Select
                            placeholder="Page Size"
                            value={pageSize}
                            onChange={(value) => {
                                setPageSize(value);
                                setCurrentPage(1);
                            }}
                            style={{ width: '100%' }}
                        >
                            <Option value={50}>50 per page</Option>
                            <Option value={100}>100 per page</Option>
                            <Option value={200}>200 per page</Option>
                            <Option value={500}>500 per page</Option>
                        </Select>
                    </Col>
                </Row>
            </Card>

            {/* Error Alert */}
            {error && (
                <Alert
                    message="Error Loading Events"
                    description="There was an error loading the event logs. Please try refreshing the page."
                    type="error"
                    showIcon
                    closable
                />
            )}

            {/* Events Table */}
            <Card>
                <ResponsiveTable
                    columns={columns}
                    dataSource={eventsData?.events || []}
                    rowKey="id"
                    loading={isLoading}
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: eventsData?.total || 0,
                        showSizeChanger: false,
                        showQuickJumper: true,
                        showTotal: (total, range) =>
                            `${range[0]}-${range[1]} of ${total} events`,
                        onChange: handleTableChange,
                    }}
                    scroll={{ x: 1200 }}
                    size="small"
                    rowSelection={{
                        selectedRowKeys,
                        onChange: setSelectedRowKeys,
                        getCheckboxProps: () => ({
                            disabled: false,
                        }),
                    }}
                />
            </Card>
        </div>
    );
};